import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  IconButton,
  Tooltip,
  Paper,
  Button,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tabs,
  Tab,
  CircularProgress,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  GetApp as ImportIcon,
  Publish as ExportIcon,
  Refresh as RefreshIcon,
  Help as HelpIcon,
  AutoAwesome as AutoAwesomeIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { notificationTemplateService } from '../../../services/notificationTemplateService';
import { NotificationTemplate } from '../../../types/notification-admin';
import { TemplateList } from '../../../components/admin/notifications/TemplateList';
import { TemplateEditor, TemplateFormData } from '../../../components/admin/notifications/TemplateEditor';
import { TemplatePreview } from '../../../components/admin/notifications/TemplatePreview';
import { VariableReference } from '../../../components/admin/notifications/VariableReference';
import { TemplateVersionHistory } from '../../../components/admin/notifications/TemplateVersionHistory';
import { TemplateTestDialog } from '../../../components/admin/notifications/TemplateTestDialog';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../../config/firebase';
import { User } from '../../../types';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`template-tabpanel-${index}`}
      aria-labelledby={`template-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

/**
 * NotificationTemplates Page
 * Complete template management system with CRUD, versioning, testing, and import/export
 */
const NotificationTemplates: React.FC = () => {
  const navigate = useNavigate();
  const { authState } = useAuth();
  const user = authState.user;

  // State - Templates
  const [templates, setTemplates] = useState<NotificationTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // State - Dialogs
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorMode, setEditorMode] = useState<'create' | 'edit'>('create');
  const [selectedTemplate, setSelectedTemplate] = useState<NotificationTemplate | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [variableRefOpen, setVariableRefOpen] = useState(false);
  const [versionHistoryOpen, setVersionHistoryOpen] = useState(false);
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<string | null>(null);

  // State - Import/Export
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importData, setImportData] = useState('');
  const [importing, setImporting] = useState(false);

  // State - Users (for test send)
  const [users, setUsers] = useState<User[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // State - Snackbar
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // State - Tabs
  const [activeTab, setActiveTab] = useState(0);

  /**
   * Load templates on mount
   */
  useEffect(() => {
    loadTemplates();
  }, []);

  /**
   * Load users when test dialog opens
   */
  useEffect(() => {
    if (testDialogOpen) {
      loadUsers();
    }
  }, [testDialogOpen]);

  /**
   * Load all templates
   */
  const loadTemplates = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await notificationTemplateService.getAllTemplates();
      setTemplates(data);
    } catch (err: any) {
      console.error('Error loading templates:', err);
      setError(err.message || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Load all users for test send
   */
  const loadUsers = async () => {
    setLoadingUsers(true);
    try {
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('isApproved', '==', true));
      const snapshot = await getDocs(q);
      const userData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as User[];
      setUsers(userData);
    } catch (err) {
      console.error('Error loading users:', err);
      showSnackbar('Failed to load users', 'error');
    } finally {
      setLoadingUsers(false);
    }
  };

  /**
   * Show snackbar message
   */
  const showSnackbar = (
    message: string,
    severity: 'success' | 'error' | 'info' | 'warning' = 'success'
  ) => {
    setSnackbar({ open: true, message, severity });
  };

  /**
   * Handle create new template
   */
  const handleCreateNew = () => {
    setSelectedTemplate(null);
    setEditorMode('create');
    setEditorOpen(true);
  };

  /**
   * Handle edit template
   */
  const handleEdit = (template: NotificationTemplate) => {
    setSelectedTemplate(template);
    setEditorMode('edit');
    setEditorOpen(true);
  };

  /**
   * Handle save template (create or update)
   */
  const handleSave = async (formData: TemplateFormData) => {
    if (!user) return;

    try {
      if (editorMode === 'create') {
        await notificationTemplateService.createTemplate(
          {
            type: formData.type,
            title: formData.title,
            message: formData.message,
            variables: formData.variables,
            priority: formData.priority,
            enabled: formData.enabled,
            createdBy: user.uid,
            updatedBy: user.uid,
          },
          user.uid
        );
        showSnackbar('Template created successfully', 'success');
      } else if (selectedTemplate) {
        await notificationTemplateService.updateTemplate(
          selectedTemplate.id,
          {
            title: formData.title,
            message: formData.message,
            variables: formData.variables,
            priority: formData.priority,
            enabled: formData.enabled,
          },
          user.uid,
          formData.changeNotes
        );
        showSnackbar('Template updated successfully', 'success');
      }

      await loadTemplates();
      setEditorOpen(false);
    } catch (err: any) {
      throw new Error(err.message || 'Failed to save template');
    }
  };

  /**
   * Handle delete template
   */
  const handleDeleteClick = (templateId: string) => {
    setTemplateToDelete(templateId);
    setDeleteDialogOpen(true);
  };

  /**
   * Confirm delete template
   */
  const handleDeleteConfirm = async () => {
    if (!templateToDelete) return;

    try {
      await notificationTemplateService.deleteTemplate(templateToDelete);
      showSnackbar('Template deleted successfully', 'success');
      await loadTemplates();
    } catch (err: any) {
      showSnackbar(err.message || 'Failed to delete template', 'error');
    } finally {
      setDeleteDialogOpen(false);
      setTemplateToDelete(null);
    }
  };

  /**
   * Handle toggle template status
   */
  const handleToggleStatus = async (templateId: string, enabled: boolean) => {
    if (!user) return;

    try {
      await notificationTemplateService.toggleTemplateStatus(templateId, enabled, user.uid);
      showSnackbar(`Template ${enabled ? 'enabled' : 'disabled'} successfully`, 'success');
      await loadTemplates();
    } catch (err: any) {
      showSnackbar(err.message || 'Failed to update template status', 'error');
    }
  };

  /**
   * Handle preview template
   */
  const handlePreview = (template: NotificationTemplate) => {
    setSelectedTemplate(template);
    setPreviewOpen(true);
  };

  /**
   * Handle view version history
   */
  const handleViewHistory = (templateId: string) => {
    const template = templates.find((t) => t.id === templateId);
    setSelectedTemplate(template || null);
    setVersionHistoryOpen(true);
  };

  /**
   * Handle restore version
   */
  const handleRestoreVersion = async (versionId: string) => {
    if (!user || !selectedTemplate) return;

    try {
      await notificationTemplateService.restoreTemplateVersion(
        selectedTemplate.id,
        versionId,
        user.uid
      );
      showSnackbar('Version restored successfully', 'success');
      await loadTemplates();
    } catch (err: any) {
      showSnackbar(err.message || 'Failed to restore version', 'error');
      throw err;
    }
  };

  /**
   * Handle test send
   */
  const handleTestSendClick = (template: NotificationTemplate) => {
    setSelectedTemplate(template);
    setTestDialogOpen(true);
  };

  /**
   * Handle test send submit
   */
  const handleTestSend = async (
    templateId: string,
    recipientUserId: string,
    testData: Record<string, string>
  ) => {
    try {
      await notificationTemplateService.testSendTemplate({
        templateId,
        recipientUserId,
        testData,
      });
      showSnackbar('Test notification sent successfully', 'success');
    } catch (err: any) {
      throw new Error(err.message || 'Failed to send test notification');
    }
  };

  /**
   * Handle export templates
   */
  const handleExport = async () => {
    try {
      const jsonData = await notificationTemplateService.exportTemplates();
      const blob = new Blob([jsonData], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `notification-templates-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      showSnackbar('Templates exported successfully', 'success');
    } catch (err: any) {
      showSnackbar(err.message || 'Failed to export templates', 'error');
    }
  };

  /**
   * Handle import templates
   */
  const handleImportClick = () => {
    setImportData('');
    setImportDialogOpen(true);
  };

  /**
   * Handle import file selection
   */
  const handleImportFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setImportData(e.target?.result as string);
      };
      reader.readAsText(file);
    }
  };

  /**
   * Handle import submit
   */
  const handleImportSubmit = async () => {
    if (!user || !importData) return;

    setImporting(true);
    try {
      const result = await notificationTemplateService.importTemplates(
        importData,
        user.uid,
        false // Don't overwrite existing
      );

      let message = `Imported ${result.imported} template(s)`;
      if (result.skipped > 0) {
        message += `, skipped ${result.skipped} existing`;
      }
      if (result.errors.length > 0) {
        message += `, ${result.errors.length} errors`;
      }

      showSnackbar(message, result.errors.length > 0 ? 'warning' : 'success');

      if (result.errors.length > 0) {
        console.error('Import errors:', result.errors);
      }

      await loadTemplates();
      setImportDialogOpen(false);
    } catch (err: any) {
      showSnackbar(err.message || 'Failed to import templates', 'error');
    } finally {
      setImporting(false);
    }
  };

  /**
   * Handle create default templates
   */
  const handleCreateDefaults = async () => {
    if (!user) return;

    try {
      const count = await notificationTemplateService.createDefaultTemplates(user.uid);
      if (count > 0) {
        showSnackbar(`Created ${count} default template(s)`, 'success');
        await loadTemplates();
      } else {
        showSnackbar('All default templates already exist', 'info');
      }
    } catch (err: any) {
      showSnackbar(err.message || 'Failed to create default templates', 'error');
    }
  };

  /**
   * Get enabled/disabled template counts
   */
  const enabledCount = templates.filter((t) => t.enabled).length;
  const disabledCount = templates.length - enabledCount;

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
        <Tooltip title="Back to Notification Dashboard">
          <IconButton onClick={() => navigate('/admin/notifications')} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>
        <Typography variant="h4" sx={{ fontWeight: 700, flexGrow: 1 }}>
          Notification Templates
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Variable Reference">
            <IconButton onClick={() => setVariableRefOpen(true)} color="primary">
              <HelpIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Refresh">
            <IconButton onClick={loadTemplates} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Action Buttons */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreateNew}>
            Create Template
          </Button>
          <Button
            variant="outlined"
            startIcon={<AutoAwesomeIcon />}
            onClick={handleCreateDefaults}
          >
            Create Defaults
          </Button>
          <Button variant="outlined" startIcon={<ImportIcon />} onClick={handleImportClick}>
            Import
          </Button>
          <Button
            variant="outlined"
            startIcon={<ExportIcon />}
            onClick={handleExport}
            disabled={templates.length === 0}
          >
            Export
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {templates.length} total • {enabledCount} enabled • {disabledCount} disabled
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
          <Tab label="All Templates" />
          <Tab label="Enabled Only" />
          <Tab label="Disabled Only" />
        </Tabs>
      </Paper>

      {/* Template Lists */}
      <TabPanel value={activeTab} index={0}>
        <TemplateList
          templates={templates}
          loading={loading}
          onEdit={handleEdit}
          onDelete={handleDeleteClick}
          onToggleStatus={handleToggleStatus}
          onViewHistory={handleViewHistory}
          onTestSend={handleTestSendClick}
          onPreview={handlePreview}
        />
      </TabPanel>

      <TabPanel value={activeTab} index={1}>
        <TemplateList
          templates={templates.filter((t) => t.enabled)}
          loading={loading}
          onEdit={handleEdit}
          onDelete={handleDeleteClick}
          onToggleStatus={handleToggleStatus}
          onViewHistory={handleViewHistory}
          onTestSend={handleTestSendClick}
          onPreview={handlePreview}
        />
      </TabPanel>

      <TabPanel value={activeTab} index={2}>
        <TemplateList
          templates={templates.filter((t) => !t.enabled)}
          loading={loading}
          onEdit={handleEdit}
          onDelete={handleDeleteClick}
          onToggleStatus={handleToggleStatus}
          onViewHistory={handleViewHistory}
          onTestSend={handleTestSendClick}
          onPreview={handlePreview}
        />
      </TabPanel>

      {/* Dialogs */}
      <TemplateEditor
        open={editorOpen}
        template={selectedTemplate}
        mode={editorMode}
        onClose={() => setEditorOpen(false)}
        onSave={handleSave}
      />

      <TemplatePreview
        open={previewOpen}
        template={selectedTemplate}
        onClose={() => setPreviewOpen(false)}
      />

      <VariableReference open={variableRefOpen} onClose={() => setVariableRefOpen(false)} />

      <TemplateVersionHistory
        open={versionHistoryOpen}
        templateId={selectedTemplate?.id || null}
        templateType={selectedTemplate ? `${selectedTemplate.type}` : undefined}
        onClose={() => setVersionHistoryOpen(false)}
        onRestore={handleRestoreVersion}
      />

      <TemplateTestDialog
        open={testDialogOpen}
        template={selectedTemplate}
        users={users}
        loadingUsers={loadingUsers}
        onClose={() => setTestDialogOpen(false)}
        onTestSend={handleTestSend}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Template?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this template? This action cannot be undone. Version
            history will be preserved for audit purposes.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import Dialog */}
      <Dialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Import Templates</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              Import templates from a JSON file. Existing templates will be skipped.
            </Alert>
            <input
              type="file"
              accept=".json"
              onChange={handleImportFileSelect}
              style={{ marginBottom: 16 }}
            />
            {importData && (
              <Alert severity="success">
                File loaded successfully. Click Import to continue.
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportDialogOpen(false)} disabled={importing}>
            Cancel
          </Button>
          <Button
            onClick={handleImportSubmit}
            variant="contained"
            disabled={!importData || importing}
            startIcon={importing ? <CircularProgress size={16} /> : <ImportIcon />}
          >
            {importing ? 'Importing...' : 'Import'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default NotificationTemplates;
